'use client';

// P& System Alert host. Renders the app-styled confirm/alert/prompt dialog for
// appConfirm()/appAlert()/appPrompt() (lib.js). Mounted once in app/layout.js
// next to the Toaster. One dialog at a time; a second request queues behind the
// first.
// An optional additionalAction { label, onAction } may perform an async action.
// Only a true result confirms; failure keeps the dialog open with its error.

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { HV2, _subscribeDialog, modalScrim, modalRadius, modalShadow, inputStyle, t, MenuIcon } from './lib';
import Button from './Button';
import { registerKeyboardLayer, trapLayerTab } from '@/lib/keyboard-layers.mjs';

export default function AppDialogHost() {
  const [dlg, setDlg] = useState(null); // { kind, message, title, confirmLabel, cancelLabel, danger, defaultValue, placeholder, multiline, resolve }
  const [input, setInput] = useState('');
  const queue = useRef([]);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const actionPending = useRef(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');

  useEffect(() => _subscribeDialog((req) => {
    setDlg((cur) => { if (cur) { queue.current.push(req); return cur; } return req; });
  }), []);

  const close = useCallback((result) => {
    if (actionPending.current) return;
    setDlg((cur) => {
      if (cur) {
        try {
          let out;
          if (cur.kind === 'confirm') out = !!result;
          else if (cur.kind === 'prompt') out = result ? (inputRef.current ? inputRef.current.value : '') : null;
          else out = undefined;
          cur.resolve(out);
        } catch { /* ignore */ }
      }
      const next = queue.current.shift() || null;
      return next;
    });
  }, []);

  const runAdditionalAction = async () => {
    if (actionPending.current) return;
    actionPending.current = true;
    setActionBusy(true); setActionError('');
    try {
      const ok = await dlg.additionalAction.onAction();
      actionPending.current = false;
      if (ok) close(true);
    } catch (error) {
      setActionError(error?.message || t('Opslaan mislukt'));
    } finally {
      actionPending.current = false;
      setActionBusy(false);
    }
  };

  // Seed the prompt field from defaultValue whenever a new dialog opens.
  useEffect(() => { setInput(dlg && dlg.kind === 'prompt' ? (dlg.defaultValue || '') : ''); setActionError(''); }, [dlg]);

  // Focus the field (prompt) or the primary button, and wire Escape (cancel) /
  // Enter (confirm). For a multiline prompt, Enter inserts a newline instead of
  // submitting - only the button confirms.
  useEffect(() => {
    if (!dlg) return undefined;
    const opener = document.activeElement;
    const focusEl = dlg.kind === 'prompt' ? inputRef.current : containerRef.current?.querySelector('[data-pb-confirm]');
    if (focusEl) { focusEl.focus(); if (focusEl.select) try { focusEl.select(); } catch { /* ignore */ } }
    const unregister = registerKeyboardLayer({
      element: () => containerRef.current, zIndex: 3000,
      onEscape: () => close(false),
      onTab: (e) => trapLayerTab(e, containerRef.current),
      onEnter: (e) => {
        if (dlg.kind === 'prompt' && dlg.multiline) return;
        // Let focused buttons keep their own Enter action (including Cancel).
        if (e.target.closest('button')) return;
        e.stopImmediatePropagation(); e.preventDefault(); close(true);
      },
    });
    return () => { unregister(); if (opener?.isConnected) opener.focus(); };
  }, [dlg, close]);

  if (!dlg) return null;
  const isConfirm = dlg.kind === 'confirm';
  const isPrompt = dlg.kind === 'prompt';
  const showCancel = isConfirm || isPrompt;
  const danger = !!dlg.danger;

  return (
    <div role="alertdialog" aria-modal="true" data-pa-overlay="dialog" onMouseDown={() => close(false)}
      style={{ position: 'fixed', inset: 0, zIndex: 3000, background: modalScrim, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, fontFamily: 'var(--font-geist), -apple-system, system-ui, sans-serif' }}>
      <div ref={containerRef} onMouseDown={(e) => e.stopPropagation()}
        style={{ width: 'min(420px, 94vw)', background: HV2.bg, border: `1px solid ${HV2.border}`, borderRadius: modalRadius, boxShadow: modalShadow, overflow: 'hidden' }}>
        {/* Header: the P& mark makes it unmistakably ours, not a browser chrome dialog. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', borderBottom: `1px solid ${HV2.divider}`, background: HV2.bgSub }}>
          {/* Kaal zwart P&-merk (zoals de sidebar), geen gekleurd vierkant (eigenaarsverzoek 14 juli). */}
          <span aria-hidden="true" style={{ display: 'inline-block', width: 18, height: 12, backgroundColor: HV2.ink, WebkitMask: 'url(/logo-pmark-white.png) center/contain no-repeat', mask: 'url(/logo-pmark-white.png) center/contain no-repeat', flexShrink: 0 }} />
          {dlg.title && <span style={{ fontSize: HV2.fsText, fontWeight: 650, color: HV2.ink }}>{dlg.title}</span>}
        </div>
        <div style={{ padding: isPrompt ? '16px 16px 12px' : '16px', fontSize: HV2.fsText, color: HV2.ink2, lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{dlg.message}</div>
        {dlg.additionalAction && (
          <div style={{ padding: '0 16px 12px' }}>
            <Button type="button" variant="white" size="md" disabled={actionBusy} onClick={runAdditionalAction}>{dlg.additionalAction.label}</Button>
            {actionError && <div role="alert" style={{ marginTop: 8, color: HV2.redText, fontSize: HV2.fsText }}>{actionError}</div>}
          </div>
        )}
        {isPrompt && (
          <div style={{ padding: '0 16px 4px' }}>
            {dlg.multiline
              ? <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder={dlg.placeholder || ''} rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              : <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} placeholder={dlg.placeholder || ''} style={inputStyle} />}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px 16px' }}>
          {showCancel && (
            <Button icon={<MenuIcon name="close" />} type="button" variant="white" size="md" disabled={actionBusy} onClick={() => close(false)}>
              {dlg.cancelLabel || t('Annuleren')}
            </Button>
          )}
          <Button icon={<MenuIcon name="check" />} type="button" variant={danger ? 'danger' : 'primary'} size="md" disabled={actionBusy} onClick={() => close(true)} data-pb-confirm>
            {dlg.confirmLabel || (showCancel ? t('Bevestigen') : t('Oké'))}
          </Button>
        </div>
      </div>
    </div>
  );
}
